import {closestCenter, pointerWithin} from "@dnd-kit/core";
import {elementAmount} from "../components/AreaTypes/AreaTypes";
import {arrayMove} from "@dnd-kit/sortable";
import dayjs from "../config/dayjs";

export const toUpperCase = name => {
    return name.toUpperCase();
};

export const capitalize = value => value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

export const getRootItems = (droppedItems = []) => droppedItems.filter(item => !item.parentId);

export const customCollisionDetection = (args) => {
    if (["input", "area", "text"].includes(args.active.id.split("-")[0])) {
        return pointerWithin(args);
    }

    const sortableContainers = args.droppableContainers.filter(
        container => container.id !== "main-droppable" && !String(container.id).startsWith("droppable-")
    );

    const pointerIntersections = pointerWithin({
        ...args,
        droppableContainers: sortableContainers
    });

    if (pointerIntersections.length > 0) {
        return pointerIntersections;
    }

    return closestCenter({
        ...args,
        droppableContainers: sortableContainers
    });
}

export const sortItems = (items, droppedItems, oldIndex, newIndex, oldItemAreaType, newItemAreaType) => {
    let newArray = [...items];

    if (oldItemAreaType === "single" && newItemAreaType === "single") {
        return arrayMove(droppedItems, oldIndex, newIndex);
    } else if (oldItemAreaType === "single" && newItemAreaType === "double") {
        if (oldIndex > newIndex) {
            newArray.splice(newIndex - 1, 2);
            newArray.splice(newIndex - 1, 0, droppedItems[oldIndex]);
            newArray.splice(oldIndex - 1, 1);
            newArray.splice(newIndex, 0, droppedItems[newIndex - 1], droppedItems[newIndex]);
        } else {
            return arrayMove(droppedItems, oldIndex, newIndex);
        }
    } else if (oldItemAreaType === "single" && newItemAreaType === "triple") {
        if (oldIndex > newIndex) {
            newArray.splice(oldIndex, 1);
            newArray.splice(newIndex - 2, 0, droppedItems[oldIndex]);
        } else {
            newArray.splice(oldIndex, 1);
            newArray.splice(newIndex + 1, 0, droppedItems[oldIndex]);
        }
    } else if (oldItemAreaType === "double" || oldItemAreaType === "triple") {
        const newItemAreaTypeValue = elementAmount[newItemAreaType];
        const oldItemAreaTypeValue = elementAmount[oldItemAreaType];

        let value = oldItemAreaTypeValue === 3 ? 3 : 2;

        if (newItemAreaTypeValue === 2) value -= 1;
        else if (newItemAreaTypeValue === 3) value -= 2;

        let droppedItemsArray = [];

        for (let i = oldItemAreaTypeValue; i > 0; i--) {
            droppedItemsArray.push(droppedItems[oldIndex - i + 1]);
        }

        if (oldIndex > newIndex) {
            newArray.splice(newIndex - newItemAreaTypeValue + 1, newItemAreaTypeValue);
            newArray.splice(newIndex - newItemAreaTypeValue + 1, 0, ...droppedItemsArray);
            newArray.splice(oldIndex - newItemAreaTypeValue + 1, oldItemAreaTypeValue);

            let addArray = [];

            for (let i = newItemAreaTypeValue; i > 0; i--) {
                addArray.push(droppedItems[newIndex - i + 1]);
            }

            newArray.splice(newIndex + value, 0, ...addArray);
        } else {
            const counter = oldItemAreaTypeValue === 3 ? 1 : 0;
            newArray.splice(oldIndex - (1 + counter), oldItemAreaTypeValue);
            newArray.splice(newIndex - (1 + counter), 0, ...droppedItemsArray);
        }
    }
    return newArray;
}

export const deleteChildren = (deleteItem, areaTypeValue, droppedItems = []) => {
    let parentIds = [];

    const deleteChildrenItem = droppedItems[droppedItems.indexOf(deleteItem) - 1]?.id;
    const deleteSecondChildrenItem = droppedItems[droppedItems.indexOf(deleteItem) - 2]?.id;

    parentIds.push(deleteItem.id);

    if (areaTypeValue === 2 && deleteChildrenItem !== undefined) {
        parentIds.push(deleteChildrenItem);
    } else if (areaTypeValue === 3 && deleteChildrenItem !== undefined && deleteSecondChildrenItem !== undefined) {
        parentIds.push(deleteChildrenItem);
        parentIds.push(deleteSecondChildrenItem);
    }

    return parentIds;
}

export const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

export const parseIfString = (value) => {
    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        }
        catch {
            return value;
        }
    }

    return value;
}

export const flattenPaths = (obj, prefix = "") => {
    return Object.entries(obj).flatMap(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;

        if (Array.isArray(value)) return [path];
        if (typeof value === "object" && value !== null) {
            return flattenPaths(value, path);
        }

        return [path];
    })
}

export const getLineHeight = (editor) => {
    const {from, to} = editor.state.selection;
    const values = new Set();

    editor.state.doc.nodesBetween(from, to, node => {
        if (["paragraph"].includes(node.type.name)) {
            values.add(node.attrs.lineHeight || "1.5");
        }
    });

    if (values.size === 0) return '1.5';
    if (values.size > 1) return null;
    return [...values][0];
};

export const formatTime = (commentDate) => {
    const date = dayjs(commentDate);

    return date.isToday() ? date.format("HH:mm [Today]") : date.format("HH:mm DD.MM.YYYY");
};

export const findObjectArrayPaths = (obj, prefix = "") => {
    if (!obj || typeof obj !== "object") return [];

    const result = [];

    for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;

        if (Array.isArray(value) && value.every(item => item !== null && typeof item === "object" && !Array.isArray(item))) {
            result.push({label: key, path});
        } else if (value !== null && typeof value === "object") {
            result.push(...findObjectArrayPaths(value, path));
        }
    }

    return result;
}

export const getLeafPaths = (path, value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return Object.entries(value).flatMap(([k, v]) => getLeafPaths(`${path}.${k}`, v));
    }

    return [path];
}

export const cleanDiffDocument = (node) => {
    const diffMarkNames = ['diffAdded', 'diffRemoved', 'diffModified'];
    if (!node || typeof node !== 'object') return node;

    const cleaned = {...node};

    if (cleaned.marks) {
        cleaned.marks = cleaned.marks.filter(m => !diffMarkNames.includes(m.type));
    }

    if (cleaned.content) {
        cleaned.content = cleaned.content
            .filter(child => {
                const cls = child.attrs?.class;
                return cls !== "diff-removed" && cls !== "diff-empty-removed";
            })
            .map(child => {
                const cls = child.attrs?.class;
                const cleanedChild = cls?.startsWith("diff-")
                    ? {...child, attrs: {...child.attrs, class: null}}
                    : child;
                return cleanDiffDocument(cleanedChild);
            })
    }

    return cleaned;
}

export const getChangedNodes = (checkpointContent, editorContent) => {
    const checkpointMap = new Map(checkpointContent.content.map(node => [node.attrs?.id, node]));
    const editorMap = new Map(editorContent.content.map(node => [node.attrs?.id, node]));

    const added = editorContent.content.filter(node => {
        const id = node.attrs?.id;
        const original = checkpointMap.get(id);
        if (!original) return true;

        return JSON.stringify(original) !== JSON.stringify(node);
    })

    const removed = checkpointContent.content.filter(node => {
        return !editorMap.has(node.attrs?.id);
    })

    return {added, removed};
}

export const getMergedDoc = (currentDoc, editorDoc, checkpoint, {added, removed}) => {
    const result = [...currentDoc.content];

    removed.forEach(node => {
        const index = result.findIndex(n => n.attrs?.id === node.attrs?.id);
        if (index !== -1) result.splice(index, 1);
    })

    added.forEach(node => {
        const existingIndex = result.findIndex(n => n.attrs?.id === node.attrs?.id);
        if (existingIndex !== -1) {
            result[existingIndex] = node;
        }
    })

    const newNodes = added.filter(node => !result.some(r => r.attrs?.id === node.attrs?.id));

    newNodes.forEach(node => {
        const editorIdx = editorDoc.content.findIndex(n => n.attrs?.id === node.attrs?.id);
        const prevSibling = editorDoc.content.slice(0, editorIdx).reverse().find(n => result.some(r => r.attrs?.id === n.attrs?.id));
        //
        if (prevSibling) {
            const insertAfter = result.findIndex(n => n.attrs?.id === prevSibling.attrs?.id);
            result.splice(insertAfter + 1, 0, node);
            return
        }

        result.unshift(node);
    })

    return {type: "doc", content: result};
}

export const handleSelectionChange = (setCommentButtonPos, blockSaveRef, editor, setLastSelection) => {
    const { from, to } = editor.state.selection;
    const selection = window.getSelection();

    setLastSelection({ from, to });

    if (
        !selection ||
        selection.rangeCount === 0 ||
        selection.isCollapsed ||
        blockSaveRef.current ||
        !editor.isEditable
    ) {
        setCommentButtonPos(null);
        return;
    }

    const pageEl = editor.view.dom;

    if (
        !pageEl.contains(selection.anchorNode) ||
        !pageEl.contains(selection.focusNode)
    ) {
        setCommentButtonPos(null);
        return;
    }

    const wrapper = pageEl.closest(".editor-wrapper");
    const commentsEl = wrapper?.querySelector(".comments");

    if (!wrapper || !commentsEl) {
        setCommentButtonPos(null);
        return;
    }

    const rects = selection.getRangeAt(0).getClientRects();

    if (!rects.length) {
        setCommentButtonPos(null);
        return;
    }

    let topY = Infinity;
    let bottomY = -Infinity;

    for (const rect of rects) {
        if (rect.width === 0) continue;

        topY = Math.min(topY, rect.top);
        bottomY = Math.max(bottomY, rect.bottom);
    }

    if (topY === Infinity) {
        setCommentButtonPos(null);
        return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const pageRect = pageEl.getBoundingClientRect();

    const midY =
        (topY + bottomY) / 2 -
        wrapperRect.top +
        wrapper.scrollTop;

    const rightX = pageRect.right - commentsEl.getBoundingClientRect().left;

    setCommentButtonPos({
        x: rightX,
        y: midY
    });
};